import { useT } from "@transifex/react";
import type { ReactNode } from "react";
import { useState } from "react";
import PhaseSixIcon, { type TIconNames } from "../../basic/phaseSixIcon/PhaseSixIcon";
import UserContentCover from "../../basic/userContentCover/UserContentCover";
import { ClassActivityCategory, classActivityColors } from "../../constants/ClassActivityConstants";
import useBreakpoints from "../../hooks/useBreakpoints";
import { AreaButton, ButtonColorVariant, ButtonVariant, IconPosition } from "../teacherArea/AreaButton";

export interface ClassActivityCardProps {
    id: string;
    subtitle: string;
    bookCover?: { src: string; alt?: string };

    // Used to handle the book cover for tests with custom content
    ownSubjectInfo?: { primaryLang: string; secondaryLang?: string };
    classActivityCategory?: ClassActivityCategory;
    headerLabel?: string;
    classLabel?: string;
    onPrimaryAction?: () => void;

    onEdit?: () => void;
    onDuplicate?: () => void;
    onDelete?: () => void;
    isEditDisabled?: boolean;

    subtitleBadge?: ReactNode;

    children?: ReactNode;
    ariaLabel?: string;
}

export const ClassActivityCard = (props: ClassActivityCardProps) => {
    const t = useT();
    const { isAboveMD: isDesktop } = useBreakpoints();

    const t_vocabTrainer = t("Vocab Trainer", { _tags: "ClassActivity" });
    const t_practiceAndTest = t("Practice & Test", { _tags: "ClassActivity" });
    const t_edit = t("Edit", { _tags: "ClassActivity, ScreenReaderOnly" });
    const t_duplicate = t("Duplicate", { _tags: "ClassActivity, ScreenReaderOnly" });
    const t_delete = t("Delete", { _tags: "ClassActivity, ScreenReaderOnly" });

    const t_share = t("Share", { _tags: "ClassActivity" });

    const classActivityCategoryConfig: {
        [key in ClassActivityCategory]: {
            name: string;
            buttonColor: ButtonColorVariant;
            iconName: TIconNames;
            buttonText: string;
        };
    } = {
        [ClassActivityCategory.VOCAB_TRAINING]: {
            name: t_vocabTrainer,
            buttonColor: ButtonColorVariant.SECONDARY,
            iconName: "share",
            buttonText: t_share,
        },
        [ClassActivityCategory.TEST]: {
            name: t_practiceAndTest,
            buttonColor: ButtonColorVariant.HIGH_CONTRAST,
            iconName: "share",
            buttonText: t_share,
        },
    };

    const [isExpanded, setIsExpanded] = useState(false);

    const classActivityCategory = props.classActivityCategory ?? ClassActivityCategory.TEST;
    const classActivityColor = classActivityColors[classActivityCategory];
    const config = classActivityCategoryConfig[classActivityCategory];
    const classActivityName = props.headerLabel ?? config.name;
    const primaryButtonColorVariant = config.buttonColor;
    const primaryButtonIconName = config.iconName;
    const primaryButtonText = config.buttonText;

    const handleToggleExpand = () => {
        setIsExpanded((prev) => !prev);
    };

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        props.onEdit?.();
    };

    const handleDuplicate = (e: React.MouseEvent) => {
        e.stopPropagation();
        props.onDuplicate?.();
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        props.onDelete?.();
    };

    const actionButtons = [
        props.onEdit && {
            onClick: props.isEditDisabled ? (e: React.MouseEvent) => e.stopPropagation() : handleEdit,
            icon: "edit-avatar",
            ariaLabel: t_edit,
            hoverColor: "hover:text-p6-grey-darkest",
            isDisabled: props.isEditDisabled,
            "data-testid": "activity-card-edit",
        },
        props.onDuplicate && {
            onClick: handleDuplicate,
            icon: "duplicate",
            ariaLabel: t_duplicate,
            hoverColor: "hover:text-p6-grey-darkest",
            isDisabled: false,
            "data-testid": "activity-card-duplicate",
        },
        props.onDelete && {
            onClick: handleDelete,
            icon: "trash",
            ariaLabel: t_delete,
            hoverColor: "hover:text-p6-red",
            isDisabled: false,
            "data-testid": "activity-card-delete",
        },
    ].filter(Boolean) as {
        // Use .filter(Boolean) to remove false values when handlers aren't provided
        onClick: (e: React.MouseEvent) => void;
        icon: string;
        ariaLabel: string;
        hoverColor: string;
        isDisabled: boolean;
        "data-testid": string;
    }[];

    const renderBookCover = () => (
        <div className="h-[100px] aspect-[3/4] rounded-xsmall-container overflow-hidden shrink-0">
            {props.bookCover && (
                <img
                    src={props.bookCover.src}
                    alt={props.bookCover.alt}
                    className="w-full h-full object-cover"
                />
            )}
            {props.ownSubjectInfo?.primaryLang && (
                <UserContentCover
                    primaryLanguage={props.ownSubjectInfo.primaryLang}
                    secondaryLanguage={props.ownSubjectInfo.secondaryLang}
                    small
                />
            )}
        </div>
    );

    const renderActionButtons = () => (
        <div className="flex items-center">
            {actionButtons.map((button) => (
                <button
                    key={button.icon}
                    onClick={button.onClick}
                    className={`w-[44px] h-[44px] flex items-center justify-center ${
                        button.isDisabled
                            ? "cursor-not-allowed text-p6-grey-light"
                            : `text-p6-grey-dark ${button.hoverColor} cursor-pointer`
                    }`}
                    type="button"
                    aria-label={button.ariaLabel}
                    disabled={button.isDisabled}
                    data-testid={button["data-testid"]}
                >
                    <PhaseSixIcon
                        name={button.icon}
                        className="text-xl"
                    />
                </button>
            ))}
        </div>
    );

    const renderChevron = () => (
        <div
            className={`w-[44px] h-[44px] flex items-center justify-center text-p6-grey-dark transition-all shrink-0 ${
                isExpanded ? "rotate-180" : ""
            }`}
            aria-hidden="true"
        >
            <PhaseSixIcon
                name="chevron-down"
                className="text-xl"
            />
        </div>
    );

    return (
        <div
            className={`bg-white border ${classActivityColor.border} border-l-8 rounded-medium flex flex-col w-full cursor-pointer`}
            aria-label={props.ariaLabel ?? classActivityName}
            data-testid={`activity-card-${props.id}`}
            onClick={handleToggleExpand}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                // Ignore keys originating in nested children (inputs, modal portals, buttons) —
                // otherwise Space inside a child input bubbles up here and collapses the card,
                // unmounting any popup the child is rendering.
                if (e.target !== e.currentTarget) return;
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleToggleExpand();
                }
            }}
            aria-expanded={isExpanded}
        >
            <div className="flex flex-col gap-s p-s">
                <div className="flex items-start gap-s">
                    {renderBookCover()}

                    <div className="flex flex-col gap-xs flex-1 min-w-0">
                        <span className={`font-semibold text-l ${classActivityColor.text}`}>
                            {classActivityName.toUpperCase()}
                        </span>
                        <span className="text-l text-p6-grey-dark font-semibold">{props.subtitle}</span>
                        {(props.classLabel || props.subtitleBadge) && (
                            <div className="flex flex-wrap items-center gap-x-s gap-y-xxs">
                                {props.classLabel && <p className="text-l text-p6-grey-mid">{props.classLabel}</p>}
                                {props.classLabel && props.subtitleBadge && (
                                    <span
                                        aria-hidden="true"
                                        className="text-l text-p6-grey-mid"
                                    >
                                        |
                                    </span>
                                )}
                                {props.subtitleBadge}
                            </div>
                        )}
                    </div>

                    {/* Desktop right section: CTA on top, Actions + Chevron */}
                    {isDesktop && (
                        <div
                            className={`flex flex-col items-end shrink-0 self-stretch ${
                                isExpanded ? "justify-center" : "justify-end"
                            }`}
                        >
                            {!isExpanded && props.onPrimaryAction && (
                                <div className="flex-1 flex items-start">
                                    <AreaButton
                                        variant={ButtonVariant.SECONDARY}
                                        colorVariant={primaryButtonColorVariant}
                                        iconName={primaryButtonIconName}
                                        iconPosition={IconPosition.RIGHT}
                                        role="button"
                                        onClick={props.onPrimaryAction}
                                    >
                                        {primaryButtonText}
                                    </AreaButton>
                                </div>
                            )}

                            <div className="flex items-center gap-s">
                                {renderActionButtons()}
                                {renderChevron()}
                            </div>
                        </div>
                    )}
                </div>

                {!isDesktop && (
                    <div className="flex items-center justify-between">
                        {renderActionButtons()}
                        {renderChevron()}
                    </div>
                )}
            </div>

            {isExpanded && props.children && (
                <div
                    onClick={(e) => e.stopPropagation()}
                    className="cursor-default"
                >
                    <div className="px-s">
                        <div className={`border-t-2 ${classActivityColor.border}`} />
                    </div>
                    {props.children}
                </div>
            )}
        </div>
    );
};
